import styled, { css } from "styled-components";
import { media, theme } from "../../theme/theme";
import { V3HeadlineH1, V3Paragraph } from "../Typography";
import { AuthoryColorSchemes } from "./V3ProfileHero.types";
import { ProfileView, UserProfileStyle } from "@authory-packages/core";
import { getV3ContentDimension } from "../../utils/getV3ContentDimension";
import { EmbedMode } from "../../utils/embeds";
import { TOP_MENU_HEIGHT } from "../../layout/SideBarLayout/SidebarLayout.styles";
import { AuthoryColorSchemeProps, getFontColor, UserThemableHeading, UserThemableParagraph } from "../ReusableStyledComponents/FontSetUtilities";
import { hexToRgba } from "../../utils/hexToRGBA";

type V4PHImageContainerProps = {
    $desktopSrc?: string
    $mobileSrc?: string
    $useImage: boolean
    $coverColor?: string,
    $enforceMinHeight: boolean,
    $isAboutPage: boolean,
    $profileStyle: UserProfileStyle,
    $isPrivateView: boolean,
    $useBorderRadius?: boolean,
};

export const V4PHMainWrapper = styled.div`
    padding: 0 18px;
`;

export const V4PHImageContainer = styled.div.attrs<V4PHImageContainerProps>(
    ({ $coverColor}) => ({
        style: {
            backgroundColor: $coverColor ? $coverColor :  undefined
        },
    })
) <V4PHImageContainerProps>`
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center center;
    border-radius: ${({ $useBorderRadius }) => $useBorderRadius ? "6px" : undefined};
    position: relative;
    min-height: ${({ $enforceMinHeight, $isAboutPage }) => {
        if ($isAboutPage) return undefined;
        return $enforceMinHeight ? "400px" : undefined;
    }};
    background-image: url(${({ $mobileSrc, $useImage }) => $useImage && $mobileSrc ? $mobileSrc : null});

    ${media.custom(theme.v3.layout.sidebarLayout.mobile)} {
        padding: ${({$profileStyle }) => $profileStyle === UserProfileStyle.sidebar ? "70px 0 90px 0" :  undefined};
        min-height: ${({$profileStyle, $isPrivateView, $isAboutPage }) => $profileStyle === UserProfileStyle.sidebar ? `calc(100vh - ${$isPrivateView ?TOP_MENU_HEIGHT : 0}px)` :  $isAboutPage ?  "250px":  undefined};
    }

    &:before {
        display: ${({ $useImage }) => $useImage ? "block" : "none"};
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(8, 22, 43, 0.6);
        border-radius: ${({ $useBorderRadius }) => $useBorderRadius ? "18px" : undefined};
    }

    ${media.custom(theme.breakpoints.phone)} {
        background-image: url(${({ $desktopSrc, $useImage }) => $useImage && $desktopSrc ? $desktopSrc : undefined});
    }
`;

export const V4PHContentWrapper = styled.div`
    position: relative;
`;

export const V4PHTopBar = styled.div<{ $profileStyle: UserProfileStyle, $smallPadding: boolean, $editMode?: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${({ $smallPadding, $profileStyle }) => $smallPadding ? "23px 0px" : $profileStyle === UserProfileStyle.centered ? "23px 0px 0px" : "23px 0 0 0"};

    ${media.custom(theme.v3.layout.sidebarLayout.mobile)} {
        padding: ${({ $smallPadding, $profileStyle }) => $smallPadding ? "23px 0px" : $profileStyle === UserProfileStyle.centered ? "23px 0px 28px" : "23px 0 75px 0"};
    }

    > div {
        &:first-child {
            margin-right: 25px;
            flex-grow: 1;
        }
    }
`;

export const PROFILE_HERO_PAD = 80;

export const V4PHContentMainWrapper = styled.div<{ $viewType: ProfileView, $profileStyle: UserProfileStyle, $isAboutPage: boolean }>`
    max-width: ${({ $viewType, $profileStyle, $isAboutPage }) => $profileStyle === UserProfileStyle.sidebar ? "300px" : $profileStyle === UserProfileStyle.centered ? "771px" : `${getV3ContentDimension($viewType, $isAboutPage) + PROFILE_HERO_PAD}px`};
    padding-bottom: 55px;
    margin: 0 auto;
    padding-top: 40px;

    ${media.custom(theme.v3.layout.sidebarLayout.mobile)} {
        padding-top: 0;
        padding-bottom: ${({ $profileStyle }) =>  $profileStyle === UserProfileStyle.sidebar  ? "0" : $profileStyle === UserProfileStyle.centered ? "100px" : "130px"}; 
    }
`;

const sidebarVerticalAlign = css<{ $editMode: boolean, $profileStyle?: UserProfileStyle }>`
    ${media.custom(theme.v3.layout.sidebarLayout.mobile)} {
        margin-left: ${({ $editMode, $profileStyle }) => $profileStyle === UserProfileStyle.sidebar && $editMode ? "-8px" : undefined};
    }
`;

export const V4PHAuthorMetaWrapper = styled.div<{ $colorVariant: AuthoryColorSchemes, $profileStyle: UserProfileStyle,  $editMode: boolean }>`
    display: flex;
    flex-direction: column;
    text-align: center;
    margin: 0 auto;
    word-break: break-word;

    ${sidebarVerticalAlign}

    ${media.custom(theme.v3.layout.sidebarLayout.mobile)} {
        flex-direction: ${({ $profileStyle }) => $profileStyle === UserProfileStyle.centered || $profileStyle === UserProfileStyle.sidebar ? "column" : "row"};
        text-align: ${({ $profileStyle }) => $profileStyle === UserProfileStyle.centered ? "center" : "left"};  
        align-items: ${({ $profileStyle }) =>  $profileStyle === UserProfileStyle.sidebar ? "flex-start" : $profileStyle === UserProfileStyle.leftAligned ? "center" : undefined}; 
    }

    > div {
        &:first-child {
            margin-bottom: ${({ $profileStyle }) => $profileStyle === UserProfileStyle.sidebar ? "30px" : "12px"};

            ${media.custom(theme.v3.layout.sidebarLayout.mobile)} {
                margin-right: ${({ $profileStyle }) => $profileStyle === UserProfileStyle.leftAligned ? "55px" : undefined};
            }
        }
    }
`;

export const V4PHProfileAvatarWrapper = styled.div<AuthoryColorSchemeProps & { $profileStyle: UserProfileStyle, $isColorWhite: boolean }>`
    width: 120px;
    height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ $isColorWhite }) => !$isColorWhite ? "rgba(255, 255, 255, 0.30)" : "rgba(0, 0, 0, 0.30)"}; ;
    border-radius: 50%;
    overflow: hidden;
    position: relative;
    margin: 0 auto;
    border: ${({ $colorVariant }) => {
        if($colorVariant === AuthoryColorSchemes.light) return `2px solid white`;
        return undefined;
    }};

    ${media.custom(theme.v3.layout.sidebarLayout.mobile)} {
        width: ${({ $profileStyle }) => $profileStyle === UserProfileStyle.centered  || $profileStyle === UserProfileStyle.sidebar ? "120px" : "220px"};
        height: ${({ $profileStyle }) => $profileStyle === UserProfileStyle.centered || $profileStyle === UserProfileStyle.sidebar ? "120px" : "220px"};
    }
`;

export const V4PHProfileAvatarImage = styled.img`
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0; 
    right: 0;
`;

export const MobileSocialsWrapper = styled.div<{ $profileStyle: UserProfileStyle}>`
    ${media.custom(theme.v3.layout.sidebarLayout.mobile)} {
        display: ${({ $profileStyle }) => $profileStyle !== UserProfileStyle.sidebar ? "none": undefined};
    }
`;

export const ProfileSocialsContainer = styled.div<{ $editMode: boolean, $isEmbed?: boolean, $EmbedMode?: EmbedMode, $desktopSideMargin?: boolean, $useDesktopAsColumn?: boolean, $profileStyle?: UserProfileStyle }>`
    display: flex;
    align-items: center;
    flex-wrap: ${({ $isEmbed }) => !$isEmbed ? "wrap" : "nowrap"};
    justify-content: ${({ $isEmbed, $EmbedMode }) => !$isEmbed || $EmbedMode === EmbedMode.normal ? "center" : "row"};
    flex-direction: ${({ $isEmbed }) => !$isEmbed ? "column" : "row"};

    ${sidebarVerticalAlign}
    
    ${media.custom(theme.v3.layout.sidebarLayout.mobile)} {
       flex-direction: ${({ $useDesktopAsColumn }) => $useDesktopAsColumn ? "column" : "row"};
       align-items: ${({ $useDesktopAsColumn }) => $useDesktopAsColumn ? "flex-start" : "row"};
    }

    > div {
        &:first-child {
            ${media.custom(theme.v3.layout.sidebarLayout.mobile)} {
                margin-right: ${({ $desktopSideMargin }) => $desktopSideMargin ? "12px" : undefined};
            }
        }

        &:nth-child(2) {
            margin-top: 32px;
            
            ${media.custom(theme.v3.layout.sidebarLayout.mobile)} {
                margin-top:  ${({ $useDesktopAsColumn }) => $useDesktopAsColumn ? "32px" : "0"};
                margin-left: ${({ $editMode, $useDesktopAsColumn}) => $editMode || $useDesktopAsColumn ? undefined : "12px"};
            }
        }
    }
`;

export const ModalMetadata = styled.div`
    text-align: center;
  
    ${media.custom(theme.v3.layout.sidebarLayout.mobile)} {
        padding: 0 28px;
    }

    ${V3Paragraph} {
        color:  ${({ theme }) => theme.v3.colors.grey35};
    }
`;

export const ImageAvatar = styled.div<{ background?: string, variant: UserProfileStyle, isSignupPreview?: boolean }>`
    display: inline-block;
    margin: ${({ variant }) => variant === UserProfileStyle.centered ? "0 auto" : undefined}; 
    border: 2px solid #FFFFFF;
    border-radius: 50%;
    background:  ${({ theme }) => theme ? theme.v3.colors.lightBlue : null};
    cursor: ${({ isSignupPreview }) => !isSignupPreview ? "pointer" : undefined};
`;

export const V3ProfileName = styled(V3HeadlineH1) <{ color: AuthoryColorSchemes, isSignupPreview?: boolean }>`
    color: ${({ color, theme }) => color === AuthoryColorSchemes.light ? "white" : theme.v3.colors.ink};
`;

export const SibebarLayoutAboutPagesAlignment = styled.div<{ $editMode: boolean, $profileStyle: UserProfileStyle }>`
    ${media.custom(theme.v3.layout.sidebarLayout.mobile)} {
        margin-left: ${({  $profileStyle, $editMode }) => $profileStyle === UserProfileStyle.sidebar  ? $editMode  ? "-10px" : "-8px": undefined};
    }
`;

export const AboutPageTitleWrapper = styled.div<AuthoryColorSchemeProps>`
    max-width: 771px;
    padding-bottom: 40px;
    margin: 0 auto;
    padding-top: 40px;
    text-align: center;

    ${media.custom(theme.v3.layout.sidebarLayout.mobile)} {
        padding-top: 0;
        padding-bottom: 100px; 
    }

`;

export const AboutPagesTitle = styled(V3HeadlineH1)<AuthoryColorSchemeProps>`
    margin: 0 auto;
    ${UserThemableHeading};
`;

export const ProfileHeroSidebarHR = styled.hr<AuthoryColorSchemeProps>`
    height: 1px;
    background: ${({ $colorVariant, theme, $customBackgroundColor, $customDarkShade }) => {
        return $colorVariant === AuthoryColorSchemes.userTheme ? hexToRgba(getFontColor($customBackgroundColor || theme.customization.backgroundColor, $colorVariant, $customDarkShade), 0.2 ) : "white";
    }};
    border: none;
`;

export const UserThemableDisplayName = styled(V3HeadlineH1)<AuthoryColorSchemeProps>`
    ${UserThemableHeading};
`;

export const UserThemableBio = styled(V3Paragraph)<AuthoryColorSchemeProps & { $profileStyle: UserProfileStyle }>`
    ${UserThemableParagraph};
    font-size: ${({ $profileStyle }) => $profileStyle === UserProfileStyle.leftAligned ? "20px" : undefined};
`;